import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faSave, faSpinner } from "@fortawesome/free-solid-svg-icons";
import StateManagedSelect from "react-select";
import axios from "axios";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import LoadingSpinner from "../../components/LoadingSpinner";

const CATEGORY_OPTIONS = [
  { value: "portfolio_notification", label: "Portfolio Notification", table: "tbl_portfolio_notification_template" },
  { value: "campaign", label: "Campaign", table: "tbl_template_bulk_campaign" },
  { value: "mail", label: "Mail", table: "tbl_template_mail" },
  { value: "push_notification", label: "Push Notification", table: "tbl_template_pushnotification" },
  { value: "sms", label: "SMS", table: "tbl_template_sms" },
  { value: "whatsapp", label: "WhatsApp Notification", table: "tbl_template_whatsapp_notification" },
];

const STATUS_OPTIONS = [
  { value: 1, label: "Active" },
  { value: 0, label: "Inactive" },
];

const AddConfigurationTemplate = () => {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(STATUS_OPTIONS[0]);

  const [pageLoading, setPageLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Helper to fetch templates for a given category
  const fetchTemplatesForCategory = async (categoryValue) => {
    if (!categoryValue) {
      setTemplateOptions([]);
      return [];
    }

    try {
      setTemplateLoading(true);
      const response = await axios.get(
        `${API_URL}settings/configurationsTemplates/templates`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          params: { category: categoryValue },
        }
      );

      const rawTemplates = response.data?.data || [];
      const formatted = rawTemplates.map((t) => ({
        value: Number(t.id),
        label: t.name || `Template #${t.id}`,
      }));

      setTemplateOptions(formatted);
      return formatted;
    } catch (err) {
      console.error("Error fetching category templates:", err);
      showToast.error("Failed to load templates for selected category");
      setTemplateOptions([]);
      return [];
    } finally {
      setTemplateLoading(false);
    }
  };

  // Handle Category change
  const handleCategoryChange = (categoryOption) => {
    setSelectedCategory(categoryOption);
    setSelectedTemplate([]);
    if (categoryOption?.value) {
      fetchTemplatesForCategory(categoryOption.value);
    } else {
      setTemplateOptions([]);
    }
  };

  // If editing, fetch existing configuration details
  useEffect(() => {
    const loadConfiguration = async () => {
      if (!isEditing) return;

      try {
        setPageLoading(true);
        const response = await axios.get(
          `${API_URL}settings/configurationsTemplates/get/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = response.data?.data;
        if (data) {
          const matchedCategory =
            CATEGORY_OPTIONS.find((c) => c.value === data.category) || {
              value: data.category,
              label: data.category_label || data.category,
            };
          setSelectedCategory(matchedCategory);

          const matchedStatus =
            STATUS_OPTIONS.find((s) => s.value === Number(data.status)) ||
            STATUS_OPTIONS[0];
          setSelectedStatus(matchedStatus);

          if (data.category) {
            const templates = await fetchTemplatesForCategory(data.category);
            if (data.template_id) {
              const idList = String(data.template_id)
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);

              const matchedTpls = idList.map((id) => {
                const found = templates.find((t) => Number(t.value) === Number(id));
                return found || { value: Number(id), label: `Template #${id}` };
              });
              setSelectedTemplate(matchedTpls);
            } else {
              setSelectedTemplate([]);
            }
          }
        }
      } catch (err) {
        console.error("Error loading configuration template:", err);
        showToast.error(
          err.response?.data?.message || "Failed to load configuration template"
        );
        navigate("/Settings/ConfigurationsTemplates");
      } finally {
        setPageLoading(false);
      }
    };

    loadConfiguration();
  }, [id, isEditing, token]);

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!selectedCategory?.value) {
      showToast.error("Please select a Category");
      return;
    }

    if (!selectedTemplate || selectedTemplate.length === 0) {
      showToast.error("Please select at least one Template");
      return;
    }

    try {
      setSubmitting(true);

      const templateIds = Array.isArray(selectedTemplate)
        ? selectedTemplate.map((t) => t.value).join(",")
        : String(selectedTemplate.value || "");

      const payload = {
        category: selectedCategory.value,
        template_id: templateIds,
        status: selectedStatus?.value !== undefined ? selectedStatus.value : 1,
      };

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      };

      let response;
      if (isEditing) {
        response = await axios.put(
          `${API_URL}settings/configurationsTemplates/update/${id}`,
          payload,
          config
        );
      } else {
        response = await axios.post(
          `${API_URL}settings/configurationsTemplates/save`,
          payload,
          config
        );
      }

      if (response.data?.success !== false) {
        showToast.success(
          response.data?.message ||
            `Configuration template ${isEditing ? "updated" : "saved"} successfully!`
        );
        navigate("/Settings/ConfigurationsTemplates");
      } else {
        showToast.error(response.data?.message || "Failed to save configuration");
      }
    } catch (err) {
      console.error("Error submitting configuration template:", err);
      showToast.error(
        err.response?.data?.message || "An error occurred while saving"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
      <div id="content" className="">
        {/* Header */}
        <div className="row pb-2 align-items-center">
          <div className="col-md-6">
            <h4 className="panel-title" style={{ margin: 0 }}>
              {isEditing
                ? "Edit Configuration Template"
                : "Add Configuration Template"}
            </h4>
          </div>
          <div className="col-md-6 text-right">
            <Link
              to="/Settings/ConfigurationsTemplates"
              className="btn btn-default btn-sm"
            >
              <FontAwesomeIcon icon={faArrowLeft} /> Back
            </Link>
          </div>
        </div>

        {/* Form Panel */}
        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="panel-heading border-0">
            <span className="panel-title" style={{ fontWeight: 600 }}>
              {isEditing
                ? "Edit Configuration Details"
                : "New Configuration Details"}
            </span>
          </div>

          <div className="panel-body">
            {pageLoading ? (
              <div className="text-center p-4">
                <LoadingSpinner />
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="row">
                  {/* 1st Dropdown: Category */}
                  <div className="form-group col-md-6 mb-3">
                    <label className="control-label" style={{ fontWeight: 600 }}>
                      Category <span className="text-danger">*</span>
                    </label>
                    <StateManagedSelect
                      options={CATEGORY_OPTIONS}
                      value={selectedCategory}
                      onChange={handleCategoryChange}
                      placeholder="-- Select Category --"
                      isClearable={false}
                      className="basic-single"
                      classNamePrefix="select"
                    />
                  </div>

                  {/* 2nd Dropdown: Template (Multi-select) */}
                  <div className="form-group col-md-6 mb-3">
                    <label className="control-label" style={{ fontWeight: 600 }}>
                      Template <span className="text-danger">*</span>
                      {templateLoading && (
                        <span className="text-primary ml-2" style={{ fontSize: "12px" }}>
                          <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                        </span>
                      )}
                    </label>
                    <StateManagedSelect
                      isMulti={true}
                      options={templateOptions}
                      value={selectedTemplate}
                      onChange={setSelectedTemplate}
                      placeholder={
                        !selectedCategory
                          ? "Select a category first"
                          : templateLoading
                          ? "Loading templates..."
                          : templateOptions.length === 0
                          ? "No templates found for this category"
                          : "-- Select Template(s) --"
                      }
                      isDisabled={!selectedCategory || templateLoading}
                      isClearable={true}
                      className="basic-multi-select"
                      classNamePrefix="select"
                    />
                  </div>

                  {/* Status Dropdown */}
                  <div className="form-group col-md-6 mb-3">
                    <label className="control-label" style={{ fontWeight: 600 }}>
                      Status
                    </label>
                    <StateManagedSelect
                      options={STATUS_OPTIONS}
                      value={selectedStatus}
                      onChange={setSelectedStatus}
                      placeholder="Select Status"
                      isClearable={false}
                      className="basic-single"
                      classNamePrefix="select"
                    />
                  </div>
                </div>

                <hr style={{ margin: "20px 0" }} />

                {/* Form Buttons */}
                <div className="d-flex justify-content-end">
                  <Link
                    to="/Settings/ConfigurationsTemplates"
                    className="btn btn-default mr-2"
                    style={{ marginRight: "10px" }}
                  >
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} /> {isEditing ? "Update" : "Save"} Configuration
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddConfigurationTemplate;
